import {
  AddIcon,
  ArrowDownUpdatedIcon,
  LinkIcon,
  MoreOptionsHorizontalIcon,
  UnlinkIcon,
} from "assests";
import Modal from "components/UI/Modal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AddLocationForm from "pages/Workspace/Order/OrderDetails/AddLocationForm";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedSubTierId,
  setSelectedTierType,
  toggleDeleteSubSupplierLocationModal,
  toggleIsForwarderCreateModalOpen,
  toggleIsOrderDocumentsEditable,
  toggleIsSelectedSupplier,
  toggleIsSubSupplierCreateFromOrderModalOpen,
  toggleIsSubSupplierCreateModalOpen,
  toggleIsTiersUpdateModalOpen,
  toggleSelectedOrderDocuments,
} from "store/orderSlice";
import usePermission from "hooks/usePermission";
import { useMemo, useState } from "react";
import ForwarderForm from "./ForwarderForm";
import UpdateOrderTiers from "./UpdateOrderTiers";
import SearchCreatePanelUpdateSupplyChain from "components/SearchCreatePanelUpdateSupplyChain";
import { getInitials, truncateString } from "utils/helper";
import { IconButton, Menu } from "@mui/material";
import MoreActionOptionUpdated from "components/MoreAction/MoreActionOptionUpdated";
import EditButton from "components/MoreAction/EditButton";
import DeleteButton from "components/MoreAction/DeleteButton";
import CreateButton from "components/MoreAction/CreateButton";
import {
  unLinkForwarderData,
  unLinkBulkSubsupplierData,
  unLinkSubsupplierData,
  generateVerificationZip,
} from "services/order.services";
import useCustomToaster from "hooks/useCustomToaster";
import toast from "react-hot-toast";
import CommonDiscardButtonUpdated from "components/UI/CommonDiscardButtonUpdated";
import { toggleBulkDeleteSubSupplierTierModal } from "store/globalActionsSlice";
import { toggleSubSupplierCreateModalOpenFromOrderDetails } from "store/subSupplierSlice";
import AddSubSupplier from "pages/SubSupplier/AddSubSupplier";
import DuplicateButton from "components/MoreAction/DuplicateButton";
import DownloadButton from "components/MoreAction/DownloadButton";

const UpdateSupplyChainCard = ({ refetchOrderDetails }) => {
  const { userPermission } = usePermission();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const queryClient = useQueryClient();
  const open = Boolean(anchorEl);
  const {
    selectedTierLists,
    selectedSubTierId,
    deleteSubSupplierLocationModal,
    orderDetailsData,
    isOrderDocumentsEditable,
    selectedOrderDocumentsId,
    isSubSupplierCreateModalOpen,
    isForwarderCreateModalOpen,
    isTiersUpdateModalOpen,
    selectedTierType,
    isSubSupplierCreateFromOrderModalOpen,
    isSelectedSupplier,
  } = useSelector((state) => state.orderSlice);
  const { subSupplierCreateModalOpenFromOrderDetails } = useSelector(
    (state) => state.subSupplierSlice,
  );
  const { bulkDeleteSubSupplierTierModal } = useSelector((state) => state.globalActionsSlice);
  const { user, currentWorkspace } = useSelector((state) => state.authenticationSlice);
  const { showCustomToast } = useCustomToaster();
  const [selectedSubSuppliers, setSelectedSubSuppliers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState(null); // "status" | "type" | "city"
  const [sortOrder, setSortOrder] = useState("asc"); // or "desc"

  const openCreateModal = (tierItem, subTierId, subTierType) => {
    if (tierItem === "tier_0" && subTierType === "Forwarder") {
      dispatch(toggleIsForwarderCreateModalOpen());
    } else {
      dispatch(toggleIsSubSupplierCreateModalOpen());
    }
    dispatch(setSelectedSubTierId(subTierId));
    dispatch(setSelectedTierType(subTierType));
    if (!isOrderDocumentsEditable) {
      dispatch(toggleIsOrderDocumentsEditable());
    }
    if (selectedOrderDocumentsId) {
      dispatch(toggleSelectedOrderDocuments());
    }
    setAnchorEl(null);
  };

  const handleClickOptionButton = (
    tierItem,
    orderDocumentsId,
    subTierId,
    actionType,
    subTierType,
  ) => {
    switch (actionType) {
      case "view":
        dispatch(toggleSelectedOrderDocuments(orderDocumentsId));
        dispatch(setSelectedSubTierId(subTierId));
        dispatch(setSelectedTierType(subTierType));
        if (tierItem === "tier_0" && subTierType === "Forwarder") {
          dispatch(toggleIsForwarderCreateModalOpen());
        } else {
          dispatch(toggleIsSubSupplierCreateModalOpen());
        }
        setAnchorEl(null);
        break;
      case "edit":
        dispatch(toggleSelectedOrderDocuments(orderDocumentsId));
        dispatch(setSelectedSubTierId(subTierId));
        dispatch(setSelectedTierType(subTierType));
        dispatch(toggleIsOrderDocumentsEditable());
        if (tierItem === "tier_0" && subTierType === "Forwarder") {
          dispatch(toggleIsForwarderCreateModalOpen());
        } else {
          dispatch(toggleIsSubSupplierCreateModalOpen());
        }
        setAnchorEl(null);
        break;
      default:
        break;
    }
  };

  const closeModal = () => {
    if (isSubSupplierCreateModalOpen) {
      dispatch(toggleIsSubSupplierCreateModalOpen());
    }
    if (isForwarderCreateModalOpen) {
      dispatch(toggleIsForwarderCreateModalOpen());
    }
    if (deleteSubSupplierLocationModal) {
      dispatch(toggleDeleteSubSupplierLocationModal());
    }
    if (selectedSubTierId) {
      dispatch(setSelectedSubTierId());
    }
    if (isOrderDocumentsEditable) {
      dispatch(toggleIsOrderDocumentsEditable());
    }
    if (selectedOrderDocumentsId) {
      dispatch(toggleSelectedOrderDocuments());
    }
    if (isTiersUpdateModalOpen) {
      dispatch(toggleIsTiersUpdateModalOpen());
    }
    if (selectedTierType) {
      dispatch(setSelectedTierType());
    }
    if (isSubSupplierCreateFromOrderModalOpen) {
      dispatch(toggleIsSubSupplierCreateFromOrderModalOpen());
    }
    if (isSelectedSupplier) {
      dispatch(toggleIsSelectedSupplier());
    }
    if (bulkDeleteSubSupplierTierModal) {
      dispatch(toggleBulkDeleteSubSupplierTierModal());
    }
    if (subSupplierCreateModalOpenFromOrderDetails) {
      dispatch(toggleSubSupplierCreateModalOpenFromOrderDetails());
    }
    setAnchorEl(null);
    setSelectedSubSuppliers([]);
    setSelectAll(false);
  };

  const getSubSupplierDetails = (tier, item) => {
    let result = null;

    if (
      orderDetailsData &&
      tier &&
      orderDetailsData?.transparency?.[`${tier}_sub_suppliers`] &&
      Array.isArray(orderDetailsData?.transparency?.[`${tier}_sub_suppliers`]) &&
      orderDetailsData?.transparency?.[`${tier}_sub_suppliers`].length > 0
    ) {
      const transparencyData = orderDetailsData?.transparency[`${tier}_sub_suppliers`];
      const allData = transparencyData?.find((subItem) => subItem?.sub_tier?.id === item?.value);
      if (allData && orderDetailsData.documents && orderDetailsData.documents.length > 0) {
        const docId = orderDetailsData.documents.find(
          (item) =>
            parseInt(item?.sub_supplier_id) === allData?.sub_supplier?.id &&
            parseInt(item?.sub_tier_id) === allData?.sub_tier?.id,
        )?.id;
        if (docId) {
          result = { ...allData.sub_supplier, docId };
        }
      }
    } else if (
      orderDetailsData &&
      tier &&
      orderDetailsData.transparency?.[`${tier}_sub_suppliers`] &&
      typeof orderDetailsData.transparency?.[`${tier}_sub_suppliers`] === "object" &&
      Object.keys(orderDetailsData.transparency?.[`${tier}_sub_suppliers`]).length > 0
    ) {
      const subSuppliers = orderDetailsData?.transparency[`${tier}_sub_suppliers`];

      const preparedList = Object.entries(subSuppliers).reduce(
        (acc, [subTierId, subTierSubSuppliers]) => {
          if (parseInt(subTierId) === item.value) {
            if (
              subTierSubSuppliers &&
              Array.isArray(subTierSubSuppliers) &&
              subTierSubSuppliers.length > 0
            ) {
              const modifiedData = subTierSubSuppliers.map((subTierSubSupplier) => {
                // find the document
                const docId = orderDetailsData?.documents?.find(
                  (doc) =>
                    parseInt(doc?.sub_supplier?.id) === subTierSubSupplier?.id &&
                    parseInt(doc?.sub_tier_id) === parseInt(subTierId),
                );

                if (docId) {
                  return { ...subTierSubSupplier, docId };
                }

                return subTierSubSupplier;
              });
              // return modifiedData;
              return [...acc, ...modifiedData];
            }
          }
          return acc;
        },
        [],
      );
      result = preparedList;
    }

    let result2 = null;

    if (
      orderDetailsData &&
      tier &&
      orderDetailsData?.transparency?.[`${tier}_suppliers`] &&
      Array.isArray(orderDetailsData?.transparency?.[`${tier}_suppliers`]) &&
      orderDetailsData?.transparency?.[`${tier}_suppliers`].length > 0
    ) {
      const transparencyData = orderDetailsData?.transparency[`${tier}_suppliers`];
      const allData = transparencyData?.find((subItem) => subItem?.sub_tier?.id === item?.value);
      if (allData && orderDetailsData.documents && orderDetailsData.documents.length > 0) {
        const docId = orderDetailsData.documents.find(
          (item) =>
            parseInt(item?.sub_supplier_id) === allData?.sub_supplier?.id &&
            parseInt(item?.sub_tier_id) === allData?.sub_tier?.id,
        )?.id;
        if (docId) {
          result2 = { ...allData.supplier, docId };
        }
      }
    } else if (
      orderDetailsData &&
      tier &&
      orderDetailsData.transparency?.[`${tier}_suppliers`] &&
      typeof orderDetailsData.transparency?.[`${tier}_suppliers`] === "object" &&
      Object.keys(orderDetailsData.transparency?.[`${tier}_suppliers`]).length > 0
    ) {
      const suppliers = orderDetailsData?.transparency[`${tier}_suppliers`];
      const newPreparedList2 = Object.entries(suppliers).reduce(
        (acc, [subTierId, subTierSubSuppliers]) => {
          if (parseInt(subTierId) === item.value) {
            if (
              subTierSubSuppliers &&
              Array.isArray(subTierSubSuppliers) &&
              subTierSubSuppliers.length > 0
            ) {
              const modifiedData = subTierSubSuppliers.map((subTierSubSupplier) => {
                // find the document
                const docId = orderDetailsData?.documents?.find(
                  (doc) =>
                    parseInt(doc?.supplier?.id) === parseInt(subTierSubSupplier?.id) &&
                    parseInt(doc?.sub_tier_id) === parseInt(subTierId),
                );
                if (docId) {
                  return { ...subTierSubSupplier, docId };
                }

                return subTierSubSupplier;
              });
              // return modifiedData;
              return [...acc, ...modifiedData];
            }
          }
          return acc;
        },
        [],
      );
      result2 = newPreparedList2;
    }
    return [...result, ...result2];
  };

  const handleTiersUpdateOpen = () => {
    dispatch(toggleIsTiersUpdateModalOpen());
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSelectedSubSuppliers([]);
    setSelectAll(false);
  };

  const unlinkSubsupplierSingleMutation = useMutation({
    mutationKey: ["deleteForwarder", "delete"],
    mutationFn: (data) => unLinkSubsupplierData(data),
    onMutate: () => {
      showCustomToast({ title: "Deleting..." }, "unlinkForwarder");
    },
    onSuccess: () => {
      toast.dismiss("unlinkForwarder");
      showCustomToast({ title: "Unlinked successfully" });
      refetchOrderDetails();
      closeModal();
      queryClient.invalidateQueries(["deleteForwarder", "get", "delete"]);
    },
    onError: (error) => {
      toast.dismiss("unlinkForwarder");
      closeModal();
      showCustomToast({ title: error.response.data.message || error.message });
    },
    onSettled: () => {
      toast.dismiss("unlinkForwarder");
    },
  });
  const unlinkBulkSubsupplierMutation = useMutation({
    mutationKey: ["deleteForwarder", "delete"],
    mutationFn: (data) => unLinkBulkSubsupplierData(data),
    onMutate: () => {
      showCustomToast({ title: "Deleting..." }, "unlinkForwarder");
    },
    onSuccess: () => {
      toast.dismiss("unlinkForwarder");
      showCustomToast({ title: "Unlinked successfully" });
      refetchOrderDetails();
      closeModal();
      queryClient.invalidateQueries(["deleteForwarder", "get", "delete"]);
    },
    onError: (error) => {
      toast.dismiss("unlinkForwarder");
      closeModal();
      showCustomToast({ title: error.response.data.message || error.message });
    },
    onSettled: () => {
      toast.dismiss("unlinkForwarder");
    },
  });
  const unlinkForwarderMutation = useMutation({
    mutationKey: ["deleteForwarder", "delete"],
    mutationFn: (id) => unLinkForwarderData(id),
    onMutate: () => {
      showCustomToast({ title: "Deleting..." }, "unlinkForwarder");
    },
    onSuccess: () => {
      toast.dismiss("unlinkForwarder");
      showCustomToast({ title: "Unlinked successfully" });
      refetchOrderDetails();
      closeModal();
      queryClient.invalidateQueries(["deleteForwarder", "get", "delete"]);
    },
    onError: (error) => {
      toast.dismiss("unlinkForwarder");
      showCustomToast({ title: error.response.data.message || error.message });
    },
    onSettled: () => {
      toast.dismiss("unlinkForwarder");
    },
  });
  const handleOnUnLink = (id) => {
    if (!id) return;
    unlinkSubsupplierSingleMutation.mutate(id);
  };

  const handleUnlinkForwarderButton = (id) => {
    if (!id) return;
    unlinkForwarderMutation.mutate(id);
  };

  const allSubSupplierIds = Object.keys(selectedTierLists || {}).flatMap((tierKey) =>
    selectedTierLists[tierKey].flatMap((item) => {
      const subSuppliers = getSubSupplierDetails(tierKey, item) || [];
      const isForwarder = tierKey === "tier_0" && item.label === "Forwarder";
      return subSuppliers
        .map((sub) => {
          const id = isForwarder ? sub?.docId?.forwarder?.id : sub?.docId?.id;
          return id ? { id, isForwarder } : null;
        })
        .filter(Boolean);
    }),
  );

  const handleSelectAll = (e) => {
    if (!e.target.checked || allSubSupplierIds.length === 0) {
      setSelectedSubSuppliers([]);
      setSelectAll(false);
      return;
    }

    setSelectedSubSuppliers(selectAll ? [] : allSubSupplierIds);
    setSelectAll(!selectAll);
  };

  const openBulkDeleteModal = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
    dispatch(toggleBulkDeleteSubSupplierTierModal());
  };

  const handleDelete = async () => {
    if (selectedSubSuppliers.length === 0) return;

    const forwarders = selectedSubSuppliers.filter((s) => s.isForwarder).map((s) => s.id);

    const documents = selectedSubSuppliers.filter((s) => !s.isForwarder).map((s) => s.id);

    try {
      unlinkBulkSubsupplierMutation.mutate({
        documents, // always send array, empty if none selected
        forwarders, // always send array, empty if none selected
      });
      setSelectedSubSuppliers([]);
      setSelectAll(false);
    } catch (err) {
      showCustomToast({ title: "Error deleting suppliers." });
    }
  };

  const totalRowCount = useMemo(() => {
    let count = 0;
    Object.keys(selectedTierLists || {}).forEach((tierKey) => {
      selectedTierLists[tierKey].forEach((item) => {
        const subSuppliers = getSubSupplierDetails(tierKey, item);
        if (Array.isArray(subSuppliers) && subSuppliers.length > 0) {
          count += subSuppliers.length; // Count all sub-suppliers
        } else {
          count += 1; // Count empty sub-supplier row
        }
      });
    });
    return count;
  }, [selectedTierLists]);

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort direction
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleCreateAction = () => {
    dispatch(toggleSubSupplierCreateModalOpenFromOrderDetails());
    setAnchorEl(null);
    setSelectedSubSuppliers([]);
    setSelectAll(false);
  };

  const downloadVerificationZip = useMutation({
    mutationKey: ["verificationsDownload", "downloadVerifications"],
    mutationFn: (data) => generateVerificationZip(data),
    onMutate: () => {
      showCustomToast({ title: "Downloading..." }, "downloadVerifications");
    },
    onSuccess: (res) => {
      toast.dismiss("downloadVerifications");
      showCustomToast({ title: "Downloaded successfully" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "verifications.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      queryClient.invalidateQueries(["verificationsDownload", "downloadVerifications"]);
    },
    onError: (error) => {
      toast.dismiss("downloadVerifications");
      showCustomToast({ title: error.response.data.message || error.message });
    },
  });

  const handleDownalodVerification = (docId) => {
    downloadVerificationZip.mutate({ download: "yes", docId: docId });
  };

  return (
    <>
      <Modal open={isTiersUpdateModalOpen} close={closeModal}>
        <UpdateOrderTiers
          closeModal={closeModal}
          orderDetailsData={orderDetailsData}
          refetchOrderDetails={refetchOrderDetails}
        />
      </Modal>
      <Modal open={isSubSupplierCreateModalOpen} close={closeModal}>
        <AddLocationForm
          closeModal={closeModal}
          selectedSubTierId={selectedSubTierId}
          refetchOrderDetails={refetchOrderDetails}
          canUpdateSubSupplier={userPermission("link_subsupplier.can")}
        />
      </Modal>
      <Modal open={isForwarderCreateModalOpen} close={closeModal}>
        <ForwarderForm
          closeModal={closeModal}
          selectedSubTierId={selectedSubTierId}
          refetchOrderDetails={refetchOrderDetails}
          canUpdateSubSupplier={userPermission("link_subsupplier.can")}
        />
      </Modal>
      <Modal open={bulkDeleteSubSupplierTierModal} close={closeModal}>
        <CommonDiscardButtonUpdated
          closeModal={closeModal}
          title={`Unlink ${selectedSubSuppliers.length > 1 ? "supply chain partners" : "supply chain partner"}`}
          subText1={`Are you sure you want to unlink selected ${selectedSubSuppliers.length > 1 ? "supply chain partners" : "supply chain partner"}? This action cannot be undone.`}
          className="w-full lg:max-w-[600px]"
          IconComponent={LinkIcon}
          confirmButtonText="Unlink"
          confirmButtonVariant="warning"
          confirmButtonClassName={"w-full h-11"}
          onConfirmClick={handleDelete}
          icon={
            <UnlinkIcon
              width={20}
              height={20}
              strokeColor="var(--color-icon-fill-secondary-button)"
            />
          }
        />
      </Modal>
      <Modal open={subSupplierCreateModalOpenFromOrderDetails} close={closeModal} title={""}>
        <AddSubSupplier closeModal={closeModal} fromOrderDetailsPage={false} />
      </Modal>
      <SearchCreatePanelUpdateSupplyChain
        permissionText="canAddSubSupplier"
        permissionText2="canAddSubSupplier"
        createTxt="Sub-Supplier"
        title="Supply Chain Partners"
        handleTierUpdate={handleTiersUpdateOpen}
        selectedItems={selectedSubSuppliers}
        openBulkDeleteModal={openBulkDeleteModal}
        btnText={totalRowCount}
        onBtnClick={handleCreateAction}
        classWidth="w-full md:w-[146px] whitespace-nowrap"
      />

      <div className="grid grid-cols-1 gap-12 z-[6]">
        <div className="w-full max-w-screen overflow-x-auto ">
          <table className="w-full border-spacing-0 table-auto rounded-table-custom overflow-x-auto">
            <thead className="text-sm text-left whitespace-nowrap text-global-paragraph">
              <tr className="border-b  border-t border-b-global-divider-soft border-t-global-divider-soft">
                <th className="pl-6 pr-4 py-[11.5px]">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className={`rounded-md  w-5 h-5 cursor-pointer`}
                      onChange={handleSelectAll}
                      checked={
                        allSubSupplierIds.length > 0 &&
                        selectedSubSuppliers.length === allSubSupplierIds.length
                      }
                      indeterminate={
                        selectedSubSuppliers.length > 0 &&
                        selectedSubSuppliers.length < allSubSupplierIds.length
                      }
                      // disabled={!hasPermission("deleteOrder")}
                    />
                    <div className="flex items-center">
                      <span className="mr-1 text-xs font-medium text-global-paragraph leading-[18px]">
                        Supplier Name
                      </span>
                    </div>
                  </div>
                </th>

                <th className="relative px-4  py-[11.5px]  ">
                  <div className="flex items-center">
                    <span className="mr-1 text-xs font-medium text-global-paragraph leading-[18px]">
                      Status
                    </span>
                    <ArrowDownUpdatedIcon
                      onClick={() => handleSort("status")}
                      className={`cursor-pointer ${sortBy === "status" && sortOrder === "asc" ? "rotate-180" : "rotate-0"}`}
                      color="var(--color-global-gray)"
                      width={16}
                      height={16}
                    />
                  </div>
                </th>

                <th className="relative px-4 py-[11.5px]  ">
                  <div className="flex items-center">
                    <span className="mr-1 text-xs font-medium text-global-paragraph leading-[18px]">
                      Type
                    </span>
                    <ArrowDownUpdatedIcon
                      onClick={() => handleSort("type")}
                      className={`cursor-pointer ${sortBy === "type" && sortOrder === "asc" ? "rotate-180" : "rotate-0"}`}
                      color="var(--color-global-gray)"
                      width={16}
                      height={16}
                    />
                  </div>
                </th>

                <th className="relative px-4 py-[11.5px]  ">
                  <div className="flex items-center">
                    <span className="mr-1 text-xs font-medium text-global-paragraph leading-[18px]">
                      Tier
                    </span>
                    <ArrowDownUpdatedIcon
                      onClick={() => handleSort("tier")}
                      className={`cursor-pointer ${sortBy === "tier" && sortOrder === "asc" ? "rotate-180" : "rotate-0"}`}
                      color="var(--color-global-gray)"
                      width={16}
                      height={16}
                    />
                  </div>
                </th>
                <th className="relative px-4 py-[11.5px]  ">
                  <div className="flex items-center">
                    <span className="mr-1 text-xs font-medium text-global-paragraph leading-[18px]">
                      City
                    </span>
                  </div>
                </th>
                <th className="relative px-4 py-[11.5px]  ">
                  <div className="flex items-center">
                    <span className="mr-1 text-xs font-medium text-global-paragraph leading-[18px]">
                      Action
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let rowIndex = 0;

                const allSubSuppliers = Object.keys(selectedTierLists || {})
                  .reverse()
                  .flatMap((tierKey) => {
                    const tierItems = selectedTierLists[tierKey] || [];

                    return tierItems.flatMap((item) => {
                      const subs = getSubSupplierDetails(tierKey, item) || [];

                      if (subs.length === 0) {
                        // Include empty row context
                        return [
                          {
                            sub: null,
                            tierKey,
                            itemLabel: item.label,
                            itemValue: item.value,
                          },
                        ];
                      }

                      return subs.map((sub) => ({
                        sub,
                        tierKey,
                        itemLabel: item.label,
                        itemValue: item.value,
                      }));
                    });
                  });

                // Sorting logic
                const sortedSubSuppliers = [...allSubSuppliers].sort((a, b) => {
                  if (sortBy === "type") {
                    return sortOrder === "asc"
                      ? a.itemLabel.localeCompare(b.itemLabel)
                      : b.itemLabel.localeCompare(a.itemLabel);
                  } else if (sortBy === "tier") {
                    return sortOrder === "asc"
                      ? a.tierKey.localeCompare(b.tierKey)
                      : b.tierKey.localeCompare(a.tierKey);
                  } else if (sortBy) {
                    const aValue = a.sub?.[sortBy] ?? "";
                    const bValue = b.sub?.[sortBy] ?? "";

                    return typeof aValue === "string"
                      ? sortOrder === "asc"
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue)
                      : sortOrder === "asc"
                        ? aValue - bValue
                        : bValue - aValue;
                  }

                  return 0;
                });

                return sortedSubSuppliers.map(({ sub, tierKey, itemLabel, itemValue }) => {
                  const currentIndex = rowIndex++;
                  const isForwarder = tierKey === "tier_0" && itemLabel === "Forwarder";
                  const thisId = isForwarder ? sub?.docId?.forwarder?.id : sub?.docId?.id;
                  const isSelected = selectedSubSuppliers.some((s) => s.id === thisId);

                  return (
                    <tr
                      key={`${tierKey}-${itemValue}-${currentIndex}`}
                      className={`whitespace-nowrap border-b last:border-0 border-b-global-divider-soft ${
                        currentIndex % 2 === 0 ? "bg-global-white" : "bg-white"
                      } rounded-lg text-global-paragraph text-sm font-normal cursor-default`}
                    >
                      <td className="pl-6 pr-4 py-[15.5px]">
                        <div className="flex items-center gap-3">
                          <input
                            disabled={!sub}
                            type="checkbox"
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                              setSelectedSubSuppliers((prev) =>
                                isSelected
                                  ? prev.filter((s) => s.id !== thisId)
                                  : [...prev, { id: thisId, isForwarder }],
                              );
                            }}
                            checked={isSelected}
                            className={`rounded-md border w-5 h-5 ${
                              !sub ? "cursor-not-allowed" : "cursor-pointer"
                            }`}
                          />
                          <div className="flex justify-start items-center gap-3">
                            <div
                              className={`flex uppercase justify-center items-center text-xs md:text-base font-semibold leading-6 w-8 h-8 md:h-10 md:w-10 p-2 rounded-full ${
                                sub
                                  ? "bg-[#F2F4F7] text-[#667085] ring-1 ring-global-divider-medium ring-opacity-75 ring-inset border-none"
                                  : "border border-dashed border-global-divider-medium border-opacity-75 text-global-paragraph"
                              }`}
                            >
                              {sub && sub.name ? getInitials(sub.name) : <AddIcon />}
                            </div>

                            <div className={`flex flex-col ${!sub ? "opacity-50" : ""}`}>
                              <p className="text-sm leading-5 font-medium text-global-title capitalize">
                                {sub?.name ? truncateString(sub.name) : truncateString(itemLabel)}
                              </p>
                              <p className="text-sm text-input-text-label leading-5">
                                {sub?.country ? sub?.country : tierKey.replace("_", " ")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td
                        className={`relative px-4 py-[15.5px] text-sm text-global-paragraph leading-5 ${!sub ? "opacity-50" : ""}`}
                      >
                        <div className="w-fit">
                          <div
                            className={`pl-[7px] pr-1.5 py-[1px] gap-1 flex justify-center items-center custom-card-buttom rounded-md border border-[#D0D5DD] bg-white shadow w-[84px]`}
                          >
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${
                                sub?.status === "cancelled"
                                  ? "bg-input-surface-disabled"
                                  : sub?.docId?.files?.length > 0
                                    ? "!bg-global-blue"
                                    : "!bg-global-primary"
                              }`}
                            />
                            <div className="text-[#344054] text-xs leading-[18px] font-medium capitalize">
                              {!sub
                                ? "Undefined"
                                : sub?.docId?.files?.length > 0
                                  ? "Verified"
                                  : sub?.status !== "cancelled"
                                    ? "Tracked"
                                    : "Undefined"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td
                        className={`relative px-4 py-[15.5px] text-sm text-global-paragraph leading-5 ${!sub ? "opacity-50" : ""}`}
                      >
                        <div className="w-fit">
                          <div className="px-1.5 py-[1px] flex justify-center items-center tracking-[-0.01em] custom-card-buttom rounded-md border border-[#D0D5DD] bg-white shadow w-auto">
                            <div className="text-[#344054] text-xs leading-[18px] font-medium capitalize">
                              {itemLabel}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td
                        className={`relative px-4 py-[15.5px] text-sm text-global-paragraph leading-5 ${!sub ? "opacity-50" : ""}`}
                      >
                        <div className="w-fit">
                          <div className="px-1.5 py-[1px] flex justify-center items-center tracking-[-0.01em] custom-card-buttom rounded-md border border-[#D0D5DD] bg-white shadow w-auto">
                            <div className="text-[#344054] text-xs leading-[18px] font-medium capitalize">
                              {tierKey.replace("_", " ")}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td
                        className={`relative px-4 py-[15.5px] text-sm text-global-paragraph leading-5 ${!sub ? "opacity-50" : ""}`}
                      >
                        {sub?.region && sub?.region}
                      </td>

                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(null);
                        }}
                        className="relative cursor-pointer px-4 py-[15.5px] text-sm text-global-paragraph leading-5"
                      >
                        <IconButton
                          aria-label="more"
                          id={`${currentIndex}`}
                          aria-controls={open ? "long-menu" : undefined}
                          aria-expanded={open ? "true" : undefined}
                          aria-haspopup="true"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            dispatch(setSelectedSubTierId(itemValue));
                            setSelectedSubSuppliers([]);
                            e.stopPropagation();
                          }}
                        >
                          <MoreOptionsHorizontalIcon width={20} height={20} />
                        </IconButton>

                        {selectedSubTierId === itemValue && (
                          <Menu
                            id="long-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: "top", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            elevation={0}
                            PaperProps={{
                              style: {
                                maxHeight: 45 * 4.5,
                                borderRadius: "8px",
                                border: "1px solid var(--color-border-secondary)",
                                padding: 0,
                                backgroundColor: "transparent",
                                boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.05)",
                              },
                            }}
                          >
                            <MoreActionOptionUpdated
                              createButton={
                                userPermission("link_subsupplier.can") &&
                                !sub && (
                                  <CreateButton
                                    title="Link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCreateModal(tierKey, itemValue, itemLabel);
                                    }}
                                    icon={
                                      <LinkIcon
                                        strokeColor="var(--color-global-paragraph-2)"
                                        width={16}
                                        height={16}
                                      />
                                    }
                                    className="w-[236px] !text-global-title-2"
                                  />
                                )
                              }
                              editButton={
                                userPermission("link_subsupplier.can") &&
                                sub &&
                                sub?.status !== "cancelled" && (
                                  <EditButton
                                    title="Edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClickOptionButton(
                                        tierKey,
                                        sub?.docId?.id,
                                        itemValue,
                                        "view",
                                        itemLabel,
                                      );
                                    }}
                                    className="w-[236px] !text-global-title-2"
                                  />
                                )
                              }
                              deleteButton={
                                userPermission("unlink_subsupplier.can") &&
                                sub &&
                                sub?.status !== "cancelled" && (
                                  <DeleteButton
                                    title="Unlink"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isForwarder) {
                                        handleUnlinkForwarderButton(sub?.docId?.forwarder?.id);
                                      } else {
                                        handleOnUnLink(sub?.docId?.id);
                                      }
                                    }}
                                    icon={
                                      <UnlinkIcon
                                        strokeColor="var(--color-global-paragraph-2)"
                                        width={16}
                                        height={16}
                                      />
                                    }
                                    className="w-[236px] !text-global-title-2"
                                  />
                                )
                              }
                              uploadButton={
                                sub &&
                                sub?.status !== "cancelled" &&
                                sub?.docId?.files?.length > 0 && (
                                  <DownloadButton
                                    title="Download Verification"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownalodVerification(sub?.docId?.id);
                                      // if (isForwarder) {
                                      //   handleUnlinkForwarderButton(sub?.docId?.forwarder?.id);
                                      // } else {
                                      //   handleOnUnLink(sub?.docId?.id);
                                      // }
                                    }}
                                    className="w-[236px] !text-global-title-2"
                                  />
                                )
                              }
                            />
                          </Menu>
                        )}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default UpdateSupplyChainCard;
